import { z } from 'zod'
import { normalizeExternalUrl } from '@/api/url'

export const emailSchema = z
    .string()
    .email('ui.profile.validation.emailInvalid')
    .max(255, 'ui.profile.validation.emailMaxLength')

export function validateEmail(value: unknown): true | string {
    const normalizedEmail = typeof value === 'string' ? value.trim() : ''
    const result = emailSchema.safeParse(normalizedEmail)
    if (result.success) return true
    const firstIssue = result.error.issues[0]
    return typeof firstIssue?.message === 'string'
        ? firstIssue.message
        : 'ui.profile.validation.emailInvalid'
}

export const INVALID_URL_MESSAGE = 'ui.components.urlInput.invalidUrl'
export const URL_TOO_LONG_MESSAGE = 'ui.components.urlInput.urlTooLong'
// WHY: Strapi stores URLs as `string` (varchar 255), longer values are rejected with a 400.
export const MAX_URL_LENGTH = 255

const externalUrlSchema = z.url({ protocol: /^https?$/, hostname: z.regexes.domain })

// WHY: Links are opened via normalizeExternalUrl, so a missing protocol (e.g. "linkedin.com/in/...")
// is fine as long as the result is a valid http(s) URL. Empty values count as valid (optional field).
// The length is checked on the normalized value because that is what gets stored.
export function getExternalUrlError(value: string | null | undefined): string | undefined {
    const trimmed = value?.trim()
    if (!trimmed) return undefined
    const normalized = normalizeExternalUrl(trimmed)
    if (!externalUrlSchema.safeParse(normalized).success) return INVALID_URL_MESSAGE
    if (normalized.length > MAX_URL_LENGTH) return URL_TOO_LONG_MESSAGE
    return undefined
}

export function isValidExternalUrl(value: string | null | undefined): boolean {
    return !getExternalUrlError(value)
}

export const optionalExternalUrlSchema = z
    .string()
    .superRefine((value, ctx) => {
        const message = getExternalUrlError(value)
        if (message) ctx.addIssue({ code: 'custom', message })
    })
    .nullish()

export function validateRequiredExternalUrl(value: unknown): true | string {
    const url = typeof value === 'string' ? value.trim() : ''
    if (!url) return INVALID_URL_MESSAGE
    return getExternalUrlError(url) ?? true
}
